import { useEffect, useMemo, useRef, useState } from 'react';
import { getNudosData } from 'utils/getNudosData';
import { canSendCustomerChatMessages } from 'utils/permissions';
import useCustomerChatComments from 'hooks/useCustomerChatComments';
import { TCustomerChatEntityType } from 'types/customerChat.types';
import { normalizeCustomerChatPersonLabel } from 'utils/customerChat.utils';
import { isValidFile } from 'utils/validateFiles';
import { submitCustomerChatComment } from 'views/LogisticServices/utils/general.utils';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import GenericLoader from 'components/GenericLoader/GenericLoader';
import BordOneToneIcon from 'components/NudosComponents/BordOneToneIcon/BordOneToneIcon';
import NudosButton from 'components/NudosComponents/NudosButton/NudosButton';
import CustomerChatMessage from 'components/CustomerChatMessage/CustomerChatMessage';
import { nudosTextcolor } from 'styles/styles.constants';
import { MENTION_TYPES } from 'types/dashboard.types';
import './CustomerChatPanel.scss';

interface ICustomerChatPanelProps {
  mode: TCustomerChatEntityType;
  entityId: number;
  onAfterSend?: () => void;
  isEntityCancelled?: boolean;
}

const CustomerChatPanel = ({
  mode,
  entityId,
  onAfterSend,
  isEntityCancelled = false
}: ICustomerChatPanelProps) => {
  const { messages, isLoadingList: loadingList, mutate: refreshMessages } = useCustomerChatComments({
    mode,
    entityId
  });
  const [sending, setSending] = useState<boolean>(false);
  const [draft, setDraft] = useState<string>('');
  const [imageFile, setImageFile] = useState<File | undefined>(undefined);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [attachMenuOpen, setAttachMenuOpen] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const customerChatPanelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const attachWrapRef = useRef<HTMLDivElement>(null);

  const emptyTitle = 'No hay mensajes en este chat todavía.';
  const emptySubtitle = 'Envía un mensaje para comenzar la conversación con el cliente.';
  const attachMenuUploadLabel = 'Cargar imagen';
  const dropImageHint = 'Suelta la imagen aquí';
  const sendDisabledBecauseCancelledTooltip =
    mode === MENTION_TYPES.ORDER
      ? 'No se puede enviar mensajes a una Orden cancelada'
      : 'No se puede enviar mensajes a un SL cancelado';
  const sendDisabledBecauseNoPermissionTooltip =
    'No tienes permiso para enviar mensajes en el chat con el cliente';

  const hasCustomerChatSendPermission = useMemo(() => canSendCustomerChatMessages(), []);

  const nudosData = getNudosData();
  const rawViewerUserId = nudosData?.userData?.id;
  const viewerUserId =
    rawViewerUserId != null && Number.isFinite(Number(rawViewerUserId)) ? Number(rawViewerUserId) : undefined;
  const viewerDisplayNameNorm = normalizeCustomerChatPersonLabel(
    `${nudosData?.userData?.firstName ?? ''} ${nudosData?.userData?.lastName ?? ''}`
  );
  const hasDraftContent = draft.trim().length > 0 || !!imageFile;
  const isImageAttachmentDisabled = sending || isEntityCancelled || !hasCustomerChatSendPermission;
  const canSend =
    hasCustomerChatSendPermission &&
    viewerUserId != null &&
    hasDraftContent &&
    !sending &&
    !isEntityCancelled;

  const attachImageFile = (file: File | null | undefined) => {
    if (!file || isImageAttachmentDisabled) return;
    if (!isValidFile({ file })) return;
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setAttachMenuOpen(false);
  };

  const getSendButtonTooltipTitle = (): string | undefined => {
    if (isEntityCancelled) {
      return sendDisabledBecauseCancelledTooltip;
    }

    if (!hasCustomerChatSendPermission) {
      return sendDisabledBecauseNoPermissionTooltip;
    }

    return undefined;
  };

  const handleRemoveImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(undefined);
    setImagePreview(null);
    setAttachMenuOpen(false);
  };

  const handleAttachMenuToggle = () => {
    if (sending || isEntityCancelled || !hasCustomerChatSendPermission || imagePreview) return;
    setAttachMenuOpen(prev => !prev);
  };

  const handleChooseUploadImage = () => {
    if (isEntityCancelled || !hasCustomerChatSendPermission) return;
    fileInputRef.current?.click();
    setAttachMenuOpen(false);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    attachImageFile(file);
  };

  const isFileDragEvent = (event: React.DragEvent<HTMLDivElement>): boolean =>
    event.dataTransfer.types.includes('Files');

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    if (!isFileDragEvent(event)) return;
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (!isFileDragEvent(event)) return;
    event.preventDefault();
    event.stopPropagation();
    if (isImageAttachmentDisabled) return;
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    if (!isFileDragEvent(event)) return;
    event.preventDefault();
    event.stopPropagation();
    if (isImageAttachmentDisabled) return;
    const relatedTarget = event.relatedTarget as Node;
    if (!customerChatPanelRef.current?.contains(relatedTarget)) setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    if (!isFileDragEvent(event)) return;
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    if (isImageAttachmentDisabled) return;
    attachImageFile(event.dataTransfer.files?.[0]);
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (isImageAttachmentDisabled) return;
    const items = event.clipboardData?.items;
    if (!items?.length) return;
    const imageItem = Array.from(items).find(item => item.type.indexOf('image') !== -1);
    if (!imageItem) return;
    const file = imageItem.getAsFile();
    if (!file) return;
    event.preventDefault();
    attachImageFile(file);
  };

  const handleSend = async () => {
    if (!canSend || !entityId) return;
    setSending(true);
    try {
      await submitCustomerChatComment({
        ...(mode === MENTION_TYPES.ORDER ? { orderId: entityId } : { logisticServiceId: entityId }),
        userId: viewerUserId,
        comments: draft.trim(),
        commentImage: imageFile
      });
      setDraft('');
      handleRemoveImage();
    } catch {
      displayErrorNotification({ customJSXMessage: <>No se pudo enviar el mensaje</> });
      return;
    } finally {
      setSending(false);
    }

    await refreshMessages();
    onAfterSend?.();
  };

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, loadingList]);

  useEffect(() => {
    if (sending) setAttachMenuOpen(false);
  }, [sending]);

  useEffect(() => {
    if (isEntityCancelled) setAttachMenuOpen(false);
  }, [isEntityCancelled]);

  useEffect(() => {
    if (!attachMenuOpen) return;
    const closeOnPointerDown = (event: PointerEvent) => {
      const root = attachWrapRef.current;
      if (root && !root.contains(event.target as Node)) {
        setAttachMenuOpen(false);
      }
    };
    document.addEventListener('pointerdown', closeOnPointerDown, true);
    return () => document.removeEventListener('pointerdown', closeOnPointerDown, true);
  }, [attachMenuOpen]);

  useEffect(() => {
    if (!attachMenuOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setAttachMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [attachMenuOpen]);

  return (
    <div
      ref={customerChatPanelRef}
      className="customerChatPanel"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDragging && (
        <div className="customerChatPanelDropOverlay">
          <BordOneToneIcon variant="uploadCloud" customWidth="1.8rem" stroke="currentColor" />
          <div>{dropImageHint}</div>
        </div>
      )}

      <div className="customerChatPanelBody" ref={listRef}>
        {loadingList && (
          <div className="customerChatPanelLoader">
            <GenericLoader sizeLoader={40} />
          </div>
        )}

        {!loadingList && messages.length === 0 && (
          <div className="customerChatPanelEmpty">
            <div className="customerChatPanelEmptyIcon">
              <BordOneToneIcon variant="comment" customWidth="48px" stroke="currentColor" />
            </div>
            <div className="customerChatPanelEmptyTextBlock">
              <p className="customerChatPanelEmptyLine">{emptyTitle}</p>
              <p className="customerChatPanelEmptyLine">{emptySubtitle}</p>
            </div>
          </div>
        )}

        {!loadingList &&
          messages.map((comment, index) => (
            <CustomerChatMessage
              key={comment.id ?? `msg-${index}`}
              comment={comment}
              viewerUserId={viewerUserId}
              viewerDisplayNameNorm={viewerDisplayNameNorm}
            />
          ))}
      </div>

      <div className="customerChatPanelComposer">
        {imagePreview && (
          <div className="customerChatPanelImagePreview">
            <img src={imagePreview} alt="" className="customerChatPanelImagePreviewImg" />
            <button
              type="button"
              className="customerChatPanelImagePreviewRemove"
              onClick={handleRemoveImage}
              disabled={sending || isEntityCancelled || !hasCustomerChatSendPermission}
            >
              <BordOneToneIcon variant="x" customWidth="12px" stroke={nudosTextcolor} />
            </button>
          </div>
        )}
        <div
          className={`customerChatPanelInputRow ${hasDraftContent ? 'customerChatPanelInputRowHasContent' : ''} ${isEntityCancelled || !hasCustomerChatSendPermission ? 'customerChatPanelInputRowDisabled' : ''}`}
        >
          <div className="customerChatPanelInputShell">
            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png" className="customerChatPanelFileInput" onChange={handleFileChange} />
            {!imagePreview && (
              <div className="customerChatPanelAttachWrap" ref={attachWrapRef}>
                <button
                  type="button"
                  className="customerChatPanelAttach"
                  onClick={handleAttachMenuToggle}
                  disabled={sending || isEntityCancelled || !hasCustomerChatSendPermission}
                  aria-label="Adjuntar imagen"
                  aria-expanded={attachMenuOpen}
                  aria-haspopup="true"
                >
                  <BordOneToneIcon variant="plus" customWidth="12px" stroke="currentColor" />
                </button>
                {attachMenuOpen && !sending && !isEntityCancelled && hasCustomerChatSendPermission && (
                  <div className="customerChatPanelAttachMenu" role="menu">
                    <button type="button" className="customerChatPanelAttachMenuOption" onClick={handleChooseUploadImage} role="menuitem">
                      <BordOneToneIcon variant="upload" customWidth="14px" stroke={nudosTextcolor} />
                      <span className="customerChatPanelAttachMenuLabel">{attachMenuUploadLabel}</span>
                    </button>
                  </div>
                )}
              </div>
            )}
            <textarea
              className="customerChatPanelTextarea"
              placeholder="Escribir comentario"
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onPaste={handlePaste}
              disabled={sending || isEntityCancelled || !hasCustomerChatSendPermission}
              rows={1}
            />
          </div>
          <span className="customerChatPanelSendTooltipHost" title={getSendButtonTooltipTitle()}>
            <NudosButton
              buttonText="Enviar"
              handleClick={handleSend}
              isButtonDisabled={!canSend}
              isButtonLoading={sending}
              customClassName="customerChatPanelSend"
              customWidth="6.4rem"
              customHeight="4rem"
            />
          </span>
        </div>
      </div>
    </div>
  );
};

export default CustomerChatPanel;
